'use client';

import { BubbleMenu, type Editor } from '@tiptap/react';
import { bubbleMenu, menuButton, select } from './tiptap-bubble-menu.css';
import { TiptapBubbleAi } from './tiptap-bubble-ai';

export type FontSizeOption = {
  label: string;
  value: string;
};

type TiptapBubbleMenuProps = {
  editor: Editor | null;
  isNotepadAiEditing: boolean;
  onAsk: (prompt: string) => void;
  appendTo?: () => HTMLElement | null;
};

type RichChain = ReturnType<Editor['chain']> & {
  toggleBold: () => RichChain;
  toggleItalic: () => RichChain;
  toggleUnderline: () => RichChain;
  toggleStrike: () => RichChain;
  toggleHeading: (attrs: { level: number }) => RichChain;
  toggleList: (list: 'bulletList' | 'orderedList', item: 'listItem') => RichChain;
  run: () => boolean;
};

const defaultFontSizes: FontSizeOption[] = [
  { label: '14', value: '14px' },
  { label: '16', value: '16px' },
  { label: '18', value: '18px' },
  { label: '20', value: '20px' },
];

export function TiptapBubbleMenu({
  editor,
  isNotepadAiEditing,
  onAsk,
  appendTo,
}: TiptapBubbleMenuProps) {
  if (!editor) return null;

  const chain = () => editor.chain().focus() as RichChain;

  const handleAsk = (prompt: string) => {
    editor.commands.blur();
    onAsk(prompt);
  };

  const setFontSize = (value: string) => chain().setMark('textStyle', { fontSize: value }).run();

  const currentFontSize = editor.getAttributes('textStyle').fontSize ?? '16px';

  const buttons = [
    {
      label: 'B',
      active: editor.isActive('bold'),
      onClick: () => chain().toggleBold().run(),
    },
    {
      label: 'I',
      active: editor.isActive('italic'),
      onClick: () => chain().toggleItalic().run(),
      style: { fontStyle: 'italic' },
    },
    {
      label: 'U',
      active: editor.isActive('underline'),
      onClick: () => chain().toggleUnderline().run(),
      style: { textDecoration: 'underline' },
    },
    {
      label: 'S',
      active: editor.isActive('strike'),
      onClick: () => chain().toggleStrike().run(),
      style: { textDecoration: 'line-through' },
    },
    {
      label: 'H1',
      active: editor.isActive('heading', { level: 1 }),
      onClick: () => chain().toggleHeading({ level: 1 }).run(),
    },
    {
      label: 'H2',
      active: editor.isActive('heading', { level: 2 }),
      onClick: () => chain().toggleHeading({ level: 2 }).run(),
    },
    {
      label: '• List',
      active: editor.isActive('bulletList'),
      onClick: () => chain().toggleList('bulletList', 'listItem').run(),
    },
    {
      label: '1. List',
      active: editor.isActive('orderedList'),
      onClick: () => chain().toggleList('orderedList', 'listItem').run(),
    },
  ];

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{
        duration: 100,
        appendTo: () => appendTo?.() ?? document.body,
        placement: 'bottom-start',
        offset: [0, 8],
        popperOptions: {
          modifiers: [
            {
              name: 'preventOverflow',
              options: { boundary: 'viewport', padding: 8 },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['top-start', 'top'],
                padding: 8,
              },
            },
          ],
        },
      }}
      className={bubbleMenu}
    >
      <TiptapBubbleAi editor={editor} isNotepadAiEditing={isNotepadAiEditing} onAsk={handleAsk}>
        {({ isOpen }) =>
          !isNotepadAiEditing && !isOpen ? (
            <>
              <select
                className={select}
                value={currentFontSize}
                onChange={(e) => setFontSize(e.target.value)}
              >
                {defaultFontSizes.map((option) => (
                  <option key={option.value} value={option.value}>{`${option.label}px`}</option>
                ))}
              </select>
              {buttons.map((button) => (
                <button
                  key={button.label}
                  type="button"
                  className={menuButton}
                  data-active={button.active}
                  onClick={button.onClick}
                  style={button.style}
                >
                  {button.label}
                </button>
              ))}
            </>
          ) : null
        }
      </TiptapBubbleAi>
    </BubbleMenu>
  );
}
